import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useState, useContext } from 'react';
import { AnnotationTypes } from '@atlaskit/adf-schema';
import { updateWindowSelectionAroundDraft } from '../draft/dom';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE, ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import { RendererContext as ActionsContext } from '../../RendererActionsContext';
export var Mounter = /*#__PURE__*/React.memo(function (props) {
  var Component = props.component,
    range = props.range,
    isWithinRange = props.isWithinRange,
    isAnnotationAllowed = props.isAnnotationAllowed,
    wrapperDOM = props.wrapperDOM,
    onCloseProps = props.onClose,
    clearAnnotationDraft = props.clearAnnotationDraft,
    applyAnnotationDraftAt = props.applyAnnotationDraftAt,
    documentPosition = props.documentPosition,
    applyAnnotation = props.applyAnnotation,
    createAnalyticsEvent = props.createAnalyticsEvent,
    generateIndexMatch = props.generateIndexMatch;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    draftDocumentPosition = _useState2[0],
    setDraftDocumentPosition = _useState2[1];
  var actions = useContext(ActionsContext);
  var onCreateCallback = useCallback(function (annotationId) {
    if (!isAnnotationAllowed || !documentPosition || !applyAnnotation) {
      return false;
    }
    var annotation = {
      annotationId: annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.INSERTED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        attributes: {},
        eventType: EVENT_TYPE.TRACK
      }).fire(FabricChannel.editor);
    }
    return applyAnnotation(draftDocumentPosition || documentPosition, annotation);
  }, [isAnnotationAllowed, documentPosition, applyAnnotation, draftDocumentPosition, createAnalyticsEvent]);
  var createIndexCallback = useCallback(function () {
    if (!documentPosition || !generateIndexMatch) {
      return false;
    }
    var result = generateIndexMatch(documentPosition);
    if (!result) {
      return false;
    }
    return result;
  }, [documentPosition, generateIndexMatch]);
  var applyDraftModeCallback = useCallback(function (options) {
    if (!documentPosition || !isAnnotationAllowed) {
      if (createAnalyticsEvent) {
        createAnalyticsEvent({
          action: ACTION.CREATE_NOT_ALLOWED,
          actionSubject: ACTION_SUBJECT.ANNOTATION,
          actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
          attributes: {},
          eventType: EVENT_TYPE.TRACK
        }).fire(FabricChannel.editor);
      }
      return false;
    }
    setDraftDocumentPosition(documentPosition);
    applyAnnotationDraftAt(documentPosition);
    if (createAnalyticsEvent) {
      var uniqueAnnotationsInRange = actions.getAnnotationsByPosition(range);
      createAnalyticsEvent({
        action: ACTION.OPENED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {
          overlap: uniqueAnnotationsInRange.length
        }
      }).fire(FabricChannel.editor);
    }
    window.requestAnimationFrame(function () {
      if (options.keepNativeSelection) {
        updateWindowSelectionAroundDraft(documentPosition);
      } else {
        var sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
        }
      }
    });
    var positionToAnnotate = draftDocumentPosition || documentPosition;
    if (!positionToAnnotate || !applyAnnotation || !options.annotationId) {
      return false;
    }
    var annotation = {
      annotationId: options.annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    return applyAnnotation(positionToAnnotate, annotation);
  }, [documentPosition, isAnnotationAllowed, applyAnnotationDraftAt, createAnalyticsEvent, applyAnnotation, draftDocumentPosition, actions, range]);
  var removeDraftModeCallback = useCallback(function () {
    clearAnnotationDraft();
    setDraftDocumentPosition(null);
    var sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
    }
  }, [clearAnnotationDraft]);
  var onCloseCallback = useCallback(function () {
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.CLOSED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {}
      }).fire(FabricChannel.editor);
    }
    removeDraftModeCallback();
    onCloseProps();
  }, [onCloseProps, removeDraftModeCallback, createAnalyticsEvent]);
  return /*#__PURE__*/React.createElement(Component, {
    range: range,
    isWithinRange: isWithinRange
    // Ignored via go/ees005
    // eslint-disable-next-line @atlaskit/editor/no-as-casting
    ,
    wrapperDOM: wrapperDOM.current,
    isAnnotationAllowed: isAnnotationAllowed,
    onClose: onCloseCallback,
    onCreate: onCreateCallback,
    getAnnotationIndexMatch: createIndexCallback,
    applyDraftMode: applyDraftModeCallback,
    removeDraftMode: removeDraftModeCallback
  });
});